// Toolbar Action Bar Component
// A reusable contextual action bar (shadow styles inlined — not served from dist as a standalone file)

const TOOLBAR_SHADOW_STYLES = `
:host {
  display: block;
  width: 100%;
}

.toolbar-action-bar {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  box-sizing: border-box;
  background: var(--card-bg);
  border: 2px solid var(--border-color);
  border-radius: 999px;
  padding: 0.5rem 0.875rem;
  box-shadow: var(--shadow-fun);
  isolation: isolate;
  transition: var(--transition);
  overflow: hidden;
}

.toolbar-action-bar::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 2px;
  background: linear-gradient(
    90deg,
    var(--primary-color),
    var(--accent-secondary),
    var(--accent-color)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}

.toolbar-action-bar:hover {
  box-shadow:
    var(--shadow-fun),
    0 0 0 1px rgba(var(--primary-color-rgb, 255, 107, 157), 0.12);
}

.toolbar-label {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-secondary);
  white-space: nowrap;
  user-select: none;
}

.toolbar-divider {
  flex-shrink: 0;
  align-self: stretch;
  width: 1px;
  min-height: 1.25rem;
  background: var(--border-color);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
}

::slotted([slot="actions"]) {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
}

.toolbar-action-bar[data-align="left"] .toolbar-actions,
.toolbar-action-bar[data-align="left"] ::slotted([slot="actions"]) {
  justify-content: flex-start;
}

.toolbar-action-bar[data-spacing="small"] {
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
}

.toolbar-action-bar[data-spacing="small"] .toolbar-actions,
.toolbar-action-bar[data-spacing="small"] ::slotted([slot="actions"]) {
  gap: 0.375rem;
}

.toolbar-action-bar[data-spacing="large"] {
  gap: 0.875rem;
  padding: 0.625rem 1rem;
}

.toolbar-action-bar[data-spacing="large"] .toolbar-actions,
.toolbar-action-bar[data-spacing="large"] ::slotted([slot="actions"]) {
  gap: 0.75rem;
}

@media (prefers-reduced-motion: reduce) {
  .toolbar-action-bar {
    transition: none;
  }
}
`;

const DEFAULT_LABEL = 'Actions';

class ToolbarActionBar extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.createShadow();
  }

  static get observedAttributes() {
    return ['align', 'spacing', 'label'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.shadowRoot) {
      if (name === 'label') {
        const labelEl = this.shadowRoot.querySelector('.toolbar-label');
        if (labelEl) {
          labelEl.textContent = newValue || DEFAULT_LABEL;
        }
      } else {
        this.updateStyles();
      }
    }
  }

  createShadow() {
    this.attachShadow({ mode: 'open' });

    const align = this.getAttribute('align') || 'right';
    const spacing = this.getAttribute('spacing') || 'medium';
    const label = this.getAttribute('label') || DEFAULT_LABEL;

    this.shadowRoot.innerHTML = `
      <style>${TOOLBAR_SHADOW_STYLES}</style>
      <div class="toolbar-action-bar" data-align="${align}" data-spacing="${spacing}" role="toolbar" aria-label="${this.escapeAttr(label)}">
        <span class="toolbar-label">${this.escapeHtml(label)}</span>
        <div class="toolbar-divider" aria-hidden="true"></div>
        <div class="toolbar-actions">
          <slot name="actions"></slot>
        </div>
      </div>
    `;
  }

  updateStyles() {
    const toolbar = this.shadowRoot?.querySelector('.toolbar-action-bar');
    if (toolbar) {
      const align = this.getAttribute('align') || 'right';
      const spacing = this.getAttribute('spacing') || 'medium';
      toolbar.setAttribute('data-align', align);
      toolbar.setAttribute('data-spacing', spacing);
    }
  }

  escapeHtml(text) {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  escapeAttr(text) {
    return String(text ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

customElements.define('toolbar-action-bar', ToolbarActionBar);
